import { gql, useQuery } from '@apollo/client';

import { PaginationDirection, TransactionSortKeys } from '../../../__generated__/globalTypes';
import { PATHS } from '../../../paths';
import { usePathWithDateRange } from '../../../providers/DateRangeProvider';

import {
  buildRecentTransactionsFilters,
  RECENT_TRANSACTIONS_FETCH_LIMIT,
  selectRecentTransactions,
} from './recentTransactions';
import RecentTransactionsSectionBase, { RecentTransactionFragmentDocument } from './RecentTransactionsSectionBase';

import type {
  GetRecentTransactionsQuery,
  GetRecentTransactionsQueryVariables,
} from './__generated__/RecentTransactionsSection.gql';

export const GET_RECENT_TRANSACTIONS = gql`
  query GetRecentTransactions(
    $pagination: PaginationInput!
    $filters: TransactionFilters
    $sortKey: TransactionSortKeys
  ) {
    getTransactionsForUser(pagination: $pagination, filters: $filters, sortKey: $sortKey) {
      edges {
        node {
          ...RecentTransaction
        }
      }
    }
  }
  ${RecentTransactionFragmentDocument}
`;

export interface IRecentTransactionsSectionProps {
  /** The record's merchant: the read's scope and where "View all" goes. */
  merchantId?: string | null;
  /** Scope the read to one recurring stream instead; the section then reads "Recent Payments". */
  recurringTransactionId?: string | null;
  /** The page's own transaction, dropped so the list shows the other rows. */
  excludeTransactionId?: string | null;
  className?: string;
}

/**
 * `RecentTransactionsSectionBase` wired to `getTransactionsForUser`: the newest
 * rows at the record's merchant (or in its recurring stream), one more than
 * shown so dropping the page's own record leaves a full list. "View all" goes
 * to the merchant page with the pane's current date range, as the other
 * merchant links do; a stream without a merchant lists its payments unlinked.
 * Hidden without a merchant or stream, when nothing else exists there, and on
 * a failed read.
 */
const RecentTransactionsSection = ({
  merchantId,
  recurringTransactionId,
  excludeTransactionId,
  className,
}: IRecentTransactionsSectionProps) => {
  const hasScope = Boolean(merchantId || recurringTransactionId);
  const { data, loading, error } = useQuery<GetRecentTransactionsQuery, GetRecentTransactionsQueryVariables>(
    GET_RECENT_TRANSACTIONS,
    {
      variables: {
        pagination: { limit: RECENT_TRANSACTIONS_FETCH_LIMIT, direction: PaginationDirection.Forward },
        filters: buildRecentTransactionsFilters({ merchantId, recurringTransactionId }),
        sortKey: TransactionSortKeys.Date,
      },
      skip: !hasScope,
    }
  );
  const addDateRangeToPath = usePathWithDateRange();

  // ApolloProvider's errorLink already reports the failure; a secondary section hides rather than breaking the page.
  if (!hasScope || error) {
    return null;
  }

  const transactions = selectRecentTransactions(
    data?.getTransactionsForUser.edges.map((edge) => edge.node) ?? [],
    excludeTransactionId
  );

  return (
    <RecentTransactionsSectionBase
      title={recurringTransactionId ? 'Recent Payments' : 'Recent Transactions'}
      transactions={transactions}
      viewAllPath={merchantId ? addDateRangeToPath(PATHS.MERCHANT(merchantId)) : undefined}
      loading={loading}
      className={className}
    />
  );
};

export default RecentTransactionsSection;
